import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useParams } from "@tanstack/react-router"
import {
	IconBookOpen4Outline18 as BookOpen,
	IconCubeOutline18 as Cube,
	IconLifeRingOutline18 as LifeRing,
	IconRepeatOutline18 as Repeat,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"
import { z } from "zod"

import type { SsoSettings } from "@productlane/zero"
import { scale } from "@productlane/lib/src/browser"
import {
	Alert,
	AlertDialog,
	Button,
	Dialog,
	Form,
	FormInput,
	Loader,
	SecretReveal,
	SubmitButton,
	Switch,
	useZodForm,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "@/screens/settings/settings-header"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { ROOT_SSO_ID, SSO_LIST_PATH } from "./sso-targets"

const FormSchema = z.object({
	tokenEndpoint: z
		.string()
		.url("Invalid URL format")
		.refine(
			(url) => url.startsWith("https://"),
			"URL must use the HTTPS protocol",
		),
})

export function SsoDetailPage() {
	const { portalId } = useParams({ strict: false })
	const workspace = useZeroWorkspace()
	const [portalSso] = useQuery(queries.portalInstanceSso())
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const [secret, setSecret] = useState("")

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: { tokenEndpoint: "" },
	})
	const activateSsoMutation = api.workspace.activateSso.useMutation({})

	const isRoot = portalId === ROOT_SSO_ID
	const instance = useMemo(
		() => portalSso.find((i) => i.id === portalId),
		[portalSso, portalId],
	)

	const closeCreateModal = () => {
		setIsCreateOpen(false)
		setTimeout(() => {
			setSecret("")
			form.reset()
		}, 200)
	}

	if (!workspace) {
		return (
			<>
				<SettingsHeader title="SSO" />
				<SettingsContainer>
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				</SettingsContainer>
			</>
		)
	}

	if (!isRoot && !instance) {
		return (
			<>
				<MetaInfo title="SSO" />
				<SettingsHeader
					title="Portal not found"
					subtitle="It was deleted, or the link points at another workspace."
				/>
				<Link
					to={SSO_LIST_PATH}
					className="text-sm font-medium text-secondary hover:text-primary"
				>
					Back to SSO
				</Link>
			</>
		)
	}

	const name = isRoot ? "Root portal" : (instance?.name ?? "Portal")
	const settings = isRoot
		? (workspace.ssoSettings ?? null)
		: (instance?.ssoSettings ?? null)
	const portalInstanceId = isRoot ? null : (portalId ?? null)

	return (
		<>
			<MetaInfo title={`SSO - ${name}`} />

			<SettingsHeader
				title={name}
				subtitle={
					isRoot
						? "Authenticate visitors on the root portal with your own system."
						: `Authenticate visitors on ${name} with its own system. Without a connection here, this portal uses the root portal's.`
				}
				docsArticle="65443d05-a78c-4979-9da3-0a43e3b35f37"
			/>

			<div className="max-w-2xl">
				{settings ? (
					<UpdateSso data={{ settings }} portalInstanceId={portalInstanceId} />
				) : hasRequiredPlan ? (
					<div>
						<Button onClick={() => setIsCreateOpen(true)}>Enable SSO</Button>
					</div>
				) : (
					<UpgradeButton required={["scale", "business", "enterprise"]} />
				)}
			</div>

			<Dialog
				isOpen={isCreateOpen}
				onOpenChange={closeCreateModal}
				title={secret ? "Save your secret" : `Activate SSO for ${name}`}
			>
				{!secret ? (
					<Form
						form={form}
						onSubmit={async (values) => {
							const result = await activateSsoMutation.mutateAsync({
								tokenEndpoint: values.tokenEndpoint,
								portalInstanceId,
							})
							setSecret(result.secret)
							await navigator.clipboard.writeText(result.secret)
							toast.success({ title: "Secret copied" })
						}}
						className="space-y-4"
					>
						<FormInput
							name="tokenEndpoint"
							label="Token endpoint"
							placeholder="https://example.com/api/productlane"
						/>
						<SubmitButton>Activate</SubmitButton>
					</Form>
				) : (
					<SecretReveal
						value={secret}
						description="This secret authenticates your SSO endpoint with Productlane."
						onCopied={() => toast.success({ title: "Secret copied" })}
						onClose={closeCreateModal}
					/>
				)}
			</Dialog>
		</>
	)
}

interface UpdateSsoProps {
	data: {
		settings: SsoSettings
	}
	portalInstanceId?: string | null
}

const UpdateSsoFormSchema = z.object({
	tokenEndpoint: z.string().min(1),
	isEnabled: z.boolean(),
	isPortalPrivate: z.boolean(),
	isDocsPrivate: z.boolean(),
	isRoadmapPrivate: z.boolean(),
	isChangelogPrivate: z.boolean(),
	isRequestsPrivate: z.boolean(),
})

function UpdateSso(props: UpdateSsoProps) {
	const form = useZodForm({
		schema: UpdateSsoFormSchema,
		defaultValues: {
			tokenEndpoint: props.data.settings.tokenEndpoint,
			isEnabled: props.data.settings.isEnabled,
			isPortalPrivate: props.data.settings.isPortalPrivate,
			isDocsPrivate: props.data.settings.isDocsPrivate,
			isRoadmapPrivate: props.data.settings.isRoadmapPrivate,
			isChangelogPrivate: props.data.settings.isChangelogPrivate,
			isRequestsPrivate: props.data.settings.isRequestsPrivate,
		},
	})
	const ctx = api.useUtils()
	const updateSsoMutation = api.workspace.updateSso.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})

	const persist = async (
		values: Partial<z.infer<typeof UpdateSsoFormSchema>>,
	) => {
		const next = { ...form.getValues(), ...values }
		await updateSsoMutation.mutateAsync({
			portalInstanceId: props.portalInstanceId ?? null,
			tokenEndpoint: next.tokenEndpoint,
			isPortalPrivate: next.isPortalPrivate,
			isEnabled: next.isEnabled,
			isDocsPrivate: next.isDocsPrivate,
			isRoadmapPrivate: next.isRoadmapPrivate,
			isChangelogPrivate: next.isChangelogPrivate,
			isRequestsPrivate: next.isRequestsPrivate,
		})
	}
	const deactivateSsoMutation = api.workspace.deactivateSso.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
			toast.success({
				title: "SSO successfully deactivated",
			})
		},
	})
	const rotateSecretMutation = api.workspace.rotateSsoSecret.useMutation()
	const [isConfirmDeleteOpen, setConfirmDelete] = useState(false)
	const [isConfirmRotateOpen, setConfirmRotate] = useState(false)
	const [rotatedSecret, setRotatedSecret] = useState("")

	const isPortalPrivate = form.watch("isPortalPrivate")
	const tokenEndpoint = form.watch("tokenEndpoint")

	const sectionNames = [
		"isDocsPrivate",
		"isRoadmapPrivate",
		"isChangelogPrivate",
		"isRequestsPrivate",
	] as const

	const handleSectionToggle = (
		name: (typeof sectionNames)[number],
		value: boolean,
	) => {
		const allPrivate = sectionNames.every((s) =>
			s === name ? value : form.getValues(s),
		)
		if (allPrivate) {
			form.setValue("isPortalPrivate", true)
			sectionNames.forEach((s) => form.setValue(s, false))
			void persist({
				isPortalPrivate: true,
				isDocsPrivate: false,
				isRoadmapPrivate: false,
				isChangelogPrivate: false,
				isRequestsPrivate: false,
			})
			return
		}
		form.setValue(name, value)
		void persist({ [name]: value })
	}

	useDebounce(
		() => {
			if (form.formState.dirtyFields.tokenEndpoint && tokenEndpoint) {
				void persist({ tokenEndpoint })
			}
		},
		700,
		[tokenEndpoint],
	)

	const sectionToggles = [
		{
			name: "isDocsPrivate" as const,
			Icon: BookOpen,
			title: "Make Docs private",
			description: "Require SSO authentication to access the docs section.",
		},
		{
			name: "isRoadmapPrivate" as const,
			Icon: Cube,
			title: "Make Feature requests private",
			description:
				"Require SSO authentication to access the feature requests section.",
		},
		{
			name: "isChangelogPrivate" as const,
			Icon: Repeat,
			title: "Make Changelog private",
			description:
				"Require SSO authentication to access the changelog section.",
		},
		{
			name: "isRequestsPrivate" as const,
			Icon: LifeRing,
			title: "Make Support Portal private",
			description:
				"Require SSO authentication to access the support portal section.",
		},
	]

	return (
		<Form
			form={form}
			onSubmit={() => undefined}
			className="flex flex-col gap-4"
		>
			<SettingsContainer className="divide-y-0">
				<SettingItem
					title="Enable SSO"
					description="When activated, users are automatically redirected to your organization's SSO login page."
					action={
						<Switch
							name="isEnabled"
							checked={form.watch("isEnabled")}
							onCheckedChange={(value) => {
								form.setValue("isEnabled", value)
								void persist({ isEnabled: value })
							}}
						/>
					}
					className="border-b pb-4"
				/>
				<SettingItem
					title="Make entire portal private"
					className="divide-y-0"
					description="When activated, the entire portal requires SSO authentication. Disable this to control access per section."
					action={
						<Switch
							name="isPortalPrivate"
							checked={isPortalPrivate}
							onCheckedChange={(value) => {
								form.setValue("isPortalPrivate", value)
								void persist({ isPortalPrivate: value })
							}}
						/>
					}
				/>

				{isPortalPrivate ? (
					<div>
						<Alert
							variant="info"
							header="Hidden from search engines"
							className="mt-3"
						>
							A fully private portal is blocked from indexing, so it stops
							appearing in Google and other search results.
						</Alert>
					</div>
				) : (
					<div className="flex flex-col gap-3 border-gray-200 pl-8 pt-2 dark:border-gray-700/80">
						{sectionToggles.map((section) => (
							<SettingItem
								key={section.name}
								Icon={section.Icon}
								title={section.title}
								description={section.description}
								action={
									<Switch
										name={section.name}
										checked={form.watch(section.name)}
										onCheckedChange={(value) =>
											handleSectionToggle(section.name, value)
										}
									/>
								}
							/>
						))}
					</div>
				)}
			</SettingsContainer>

			<SettingsSection
				title="Credentials"
				description="The token endpoint Productlane redirects to, and the secret it signs requests with."
			/>
			<SettingsContainer className="flex flex-col gap-4 divide-none">
				<FormInput
					name="tokenEndpoint"
					label="Token endpoint"
					onBlur={() => {
						if (form.formState.dirtyFields.tokenEndpoint && tokenEndpoint) {
							void persist({ tokenEndpoint })
						}
					}}
				/>
				<SettingItem
					title="Rotate secret"
					description="Generate a new signing secret. The current secret stops working immediately."
					action={
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setConfirmRotate(true)}
						>
							Rotate
						</Button>
					}
				/>
			</SettingsContainer>

			<div className="mt-2 flex justify-end gap-4">
				<AlertDialog
					isOpen={isConfirmDeleteOpen}
					onOpenChange={setConfirmDelete}
					confirmText="Deactivate"
					title="Are you sure?"
					description="Do you really want to deactivate SSO?"
					onCancel={() => {
						setConfirmDelete(false)
					}}
					onConfirm={async () => {
						await deactivateSsoMutation.mutateAsync({
							portalInstanceId: props.portalInstanceId ?? null,
						})
					}}
				/>
				<AlertDialog
					isOpen={isConfirmRotateOpen}
					onOpenChange={setConfirmRotate}
					confirmText="Rotate secret"
					title="Rotate SSO secret?"
					description="The current secret stops working immediately. Update your SSO endpoint with the new secret right after."
					onCancel={() => {
						setConfirmRotate(false)
					}}
					onConfirm={async () => {
						const result = await rotateSecretMutation.mutateAsync({
							portalInstanceId: props.portalInstanceId ?? null,
						})
						setConfirmRotate(false)
						setRotatedSecret(result.secret)
						await navigator.clipboard.writeText(result.secret)
						toast.success({ title: "Secret copied" })
					}}
				/>
				<Button
					variant="outline"
					onClick={() => {
						setConfirmDelete(true)
					}}
					className="max-h-8 min-w-12"
				>
					Deactivate
				</Button>
			</div>

			<Dialog
				isOpen={!!rotatedSecret}
				onOpenChange={() => setRotatedSecret("")}
				title="Save your new secret"
			>
				<SecretReveal
					value={rotatedSecret}
					description="This secret authenticates your SSO endpoint with Productlane."
					onCopied={() => toast.success({ title: "Secret copied" })}
					onClose={() => setRotatedSecret("")}
				/>
			</Dialog>
		</Form>
	)
}
